import { defineStore, storeToRefs } from "pinia";
import { ref, computed } from "vue";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@composables/handleError.js";
import { deepCopy } from "@utils/toolBox";
import { useSurveyStore } from "@stores/survey/survey";
import { useAiInsightConversationMessagesStore } from "@stores/survey/assistant/aiInsightConversationMessages";
import type { AiInsightConversation } from "@/types";
import { captureEvent } from "@/lib/analytics";

export const useAiInsightConversationsStore = defineStore("aiInsightConversations", () => {
  // state
  const conversations = ref<AiInsightConversation[]>([]);
  const initialConversations = ref<AiInsightConversation[]>([]);
  const selectedConversation = ref<Partial<AiInsightConversation>>({});
  const selectedConversationIndex = ref(-1);

  // stores
  const { token } = storeToRefs(useSurveyStore());
  const { conversationMessages } = storeToRefs(useAiInsightConversationMessagesStore());

  // actions
  const getConversations = async () => {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/ai_insight_conversations`)
        .json<{ ai_insight_conversations: AiInsightConversation[] }>();

      if (response.value.ok) {
        const { ai_insight_conversations: fetched } = data.value;
        conversations.value = fetched;
        initialConversations.value = deepCopy(fetched);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const selectConversation = (index: number) => {
    if (index == null) return;

    selectedConversationIndex.value = index;
    selectedConversation.value = conversations.value[index] || {};
    conversationMessages.value = selectedConversation.value.messages ?? [];
  };

  const newConversation = async () => {
    const { response, data } = await useMyFetch(`/surveys/${token.value}/ai_insight_conversations/new`)
      .get()
      .json<AiInsightConversation>();

    if (response.value.ok) {
      conversations.value.push(data.value);
      initialConversations.value.push(deepCopy(data.value));
      selectConversation(conversations.value.length - 1);
    } else {
      handleError(data);
    }
  };

  const createConversation = async () => {
    const index = selectedConversationIndex.value;
    const conversation = conversations.value.at(index);
    if (!conversation || conversation.id !== null) return;

    const { response, data } = await useMyFetch(`/surveys/${token.value}/ai_insight_conversations`)
      .post({ ...conversation })
      .json<AiInsightConversation>();

    if (response.value.ok) {
      Object.assign(conversations.value.at(index), data.value);
      Object.assign(initialConversations.value.at(index), deepCopy(data.value));
      selectConversation(index);

      captureEvent("ai_insight_conversation_created");
    } else {
      handleError(data);
    }
  };

  const deleteConversation = async (index: number) => {
    const conversationToDelete = conversations.value[index];
    const { response, data } = await useMyFetch(
      `/surveys/${token.value}/ai_insight_conversations/${conversationToDelete.id}`,
    )
      .delete()
      .json();

    if (response.value.ok) {
      conversations.value.splice(index, 1);
      initialConversations.value.splice(index, 1);
      captureEvent("ai_insight_conversation_deleted");
      const newIndex = Math.min(index, conversations.value.length - 1);
      selectConversation(newIndex);
    } else {
      handleError(data);
    }
  };

  const updateConversation = async (index: number, paramsToUpdate: string[] = ["title"]) => {
    const conversationToUpdate = conversations.value[index];
    const { response, data } = await useMyFetch(
      `/surveys/${token.value}/ai_insight_conversations/${conversationToUpdate.id}`,
    )
      .put(conversationToUpdate)
      .json<AiInsightConversation>();

    if (response.value.ok) {
      paramsToUpdate.forEach((param) => {
        conversations.value[index][param] = data.value[param];
        initialConversations.value[index][param] = deepCopy(data.value[param]);
      });
    } else {
      handleError(data);
    }
  };

  const generateConversationTitle = async (conversationId: number) => {
    const index = conversations.value.findIndex((conversation) => conversation.id === conversationId);
    const conversationToUpdate = conversations.value[index];
    const { response, data } = await useMyFetch(
      `/surveys/${token.value}/ai_insight_conversations/${conversationToUpdate.id}/generate_title`,
    )
      .patch()
      .json<AiInsightConversation>();

    if (response.value.ok) {
      conversations.value[index].title = data.value.title;
      initialConversations.value[index].title = deepCopy(data.value.title);
    } else {
      handleError(data);
    }
  };

  const updateConversationTitle = (index: number, title: string) => (conversations.value[index].title = title);
  const restoreConversationTitle = (index: number) =>
    (conversations.value[index].title = initialConversations.value[index].title);
  const isConversationSelected = (index: number) => selectedConversationIndex.value === index;
  const isNewConversation = (conversation: AiInsightConversation) => conversation.id === null;
  const isGeneratingMessage = computed(() =>
    [0, 1].includes(selectedConversation.value.messages?.at(-1)?.status),
  );

  return {
    conversations,
    initialConversations,
    selectedConversation,
    selectedConversationIndex,
    isGeneratingMessage,
    getConversations,
    selectConversation,
    newConversation,
    createConversation,
    deleteConversation,
    updateConversation,
    generateConversationTitle,
    isConversationSelected,
    isNewConversation,
    updateConversationTitle,
    restoreConversationTitle,
  };
});
